import { useMemo } from 'react';
import { MapPin, Clock } from 'lucide-react';
import { StatusChip, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { usePracticesList } from '@/settings/services';
import type {
  PracticeList,
  Staff,
  WorkingHourSummary,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Practices tab — PROD-765.
 *
 * Flutter source: `staff_practices_tab.dart`. Read-only display of the
 * practices a staff member is assigned to. No edit affordance — practice
 * assignment is managed via toggle rows on the Account Details tab.
 *
 * For each assigned practice we render:
 *   - Practice image (or fallback initials)
 *   - Practice name + "Primary" badge (if isPrimary)
 *   - Locations (from `practice.locations[]`, comma-joined)
 *   - Working-hours summary line ("Mon-Sun from 12:00 AM to 11:55 PM" style)
 *
 * Practice data comes from `usePracticesList` (full org list); we filter
 * down to the ones in `staff.assignStaffToPractice[]` where `isAssign=true`.
 * If the staff member is unassigned from a practice (`isAssign=false`) it
 * doesn't appear here — that matches Flutter's `staffPracticeList` build.
 */
export interface StaffPracticesTabProps {
  draft: Staff;
}

export function StaffPracticesTab({ draft }: StaffPracticesTabProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: allPractices = [], isLoading } = usePracticesList(orgId, false);

  const assigned = useMemo(() => {
    const assignedIds = new Set(
      draft.assignStaffToPractice
        .filter((a) => a.isAssign)
        .map((a) => a.practiceId),
    );
    return allPractices.filter(
      (p) => p.practiceSetupId && assignedIds.has(p.practiceSetupId),
    );
  }, [draft.assignStaffToPractice, allPractices]);

  const headerName = nameFor(draft) || 'this staff member';

  return (
    <div className="space-y-4">
      <h2 className={SETTINGS_SECTION_TITLE}>
        {`${headerName}'s Practices`}
      </h2>
      {isLoading ? (
        <BrandLoading size={48} label="Loading practices…" />
      ) : assigned.length === 0 ? (
        <div data-slot="settings-empty" className="rounded-lg border border-dashed border-border-hairline-strong bg-surface-card px-6 py-12 text-center">
          <p data-slot="settings-empty-title" className="text-sm font-semibold text-text-default mb-1">
            No practices assigned
          </p>
          <p className="text-sm text-text-muted">
            Assign this staff member to a practice from the Account Details tab.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {assigned.map((practice) => (
            <li key={practice.practiceSetupId}>
              <PracticeCard practice={practice} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function nameFor(s: Staff): string {
  return `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim();
}

function PracticeCard({ practice }: { practice: PracticeList }) {
  const locationsLine = (practice.locations ?? [])
    .map((l) => l.name?.trim())
    .filter((n): n is string => !!n && n.length > 0)
    .join(', ');
  const hoursLine = formatWorkingHours(practice.workingHours ?? []);

  return (
    <article data-slot="settings-record" data-record-size="lg" className="flex items-start gap-4 rounded-lg border border-border-card bg-surface-card p-4 shadow-1">
      <PracticeImage url={practice.practiceImageURL} name={practice.practiceName ?? ''} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 data-slot="settings-record-title" className="text-base font-bold text-text-default truncate">
            {practice.practiceName ?? 'Practice'}
          </h3>
          {practice.isPrimary && (
            <StatusChip tone="soft" semantic="success">
              Primary
            </StatusChip>
          )}
        </div>
        {locationsLine && (
          <p className="mt-1 flex items-center gap-2 text-sm text-text-muted">
            <MapPin className="size-4 text-text-secondary" aria-hidden />
            {locationsLine}
          </p>
        )}
        {hoursLine && (
          <p className="mt-1 flex items-center gap-2 text-sm text-text-default">
            <Clock className="size-4 text-text-secondary" aria-hidden />
            {hoursLine}
          </p>
        )}
      </div>
    </article>
  );
}

function PracticeImage({ url, name }: { url: string | null; name: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        data-slot="settings-practice-square"
        className="h-12 w-12 rounded object-cover flex-shrink-0"
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div
      data-slot="settings-practice-square"
      className="h-12 w-12 rounded bg-surface-background text-text-muted flex items-center justify-center text-sm font-bold flex-shrink-0"
    >
      {initials || '?'}
    </div>
  );
}

/**
 * Render a single human line summarising the working hours. Groups
 * contiguous-time slots (e.g. all 7 weekdays with the same start/end times
 * → "Mon-Sun from 12:00 AM to 11:55 PM"). Flutter parity:
 * `staff_practices_tab.dart:55-61` uses `groupBy` keyed on
 * `"from {startTime} to {endTime}"`.
 */
function formatWorkingHours(hours: WorkingHourSummary[]): string {
  const usable = hours.filter((h) => h.startTime && h.endTime && h.startWeekDay);
  if (usable.length === 0) return '';
  // Single line for the most common case — same hours every day. If the
  // staff has variable hours per day, we collapse to the first group's
  // label (acceptable v1; refinement is a follow-up if needed).
  const first = usable[0];
  const allSame = usable.every(
    (h) => h.startTime === first.startTime && h.endTime === first.endTime,
  );
  if (allSame && usable.length === 7) {
    return `Mon-Sun from ${first.startTime} to ${first.endTime}`;
  }
  if (allSame) {
    const days = usable
      .map((h) => (h.startWeekDay ?? '').slice(0, 3))
      .filter(Boolean)
      .join(', ');
    return `${days} from ${first.startTime} to ${first.endTime}`;
  }
  return `${first.startWeekDay} from ${first.startTime} to ${first.endTime}`;
}
